import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faPlus, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import {
  Badge,
  Breadcrumb,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Pagination,
  Row,
  Table,
} from "@themesberg/react-bootstrap";
import { apiRequest } from "../utils/api";

const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);

const formatDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Add form
  const [formValues, setFormValues] = useState({ name: "", email: "", password: "" });
  const [formError, setFormError] = useState("");

  // Edit modal
  const [editingUser, setEditingUser] = useState(null);
  const [editValues, setEditValues] = useState({ name: "", email: "", password: "" });
  const [editError, setEditError] = useState("");

  // Delete confirm
  const [deletingUser, setDeletingUser] = useState(null);

  // Toggle active (admin)
  const [togglingId, setTogglingId] = useState(null);

  /* ---------- Fetch users ---------- */
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await apiRequest("/users");
        setUsers(data.users || []);
      } catch (err) {
        console.error("Failed to fetch users:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  /* ---------- Search & pagination ---------- */
  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) =>
      [u.name, u.email].filter(Boolean).join(" ").toLowerCase().includes(term)
    );
  }, [users, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + pageSize);

  /* ---------- Add user ---------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const canSave =
    formValues.name.trim().length >= 2 &&
    isValidEmail(formValues.email) &&
    formValues.password.length >= 6;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setFormError("");

    try {
      const data = await apiRequest("/users", {
        method: "POST",
        body: JSON.stringify({
          name: formValues.name.trim(),
          email: formValues.email.trim(),
          password: formValues.password,
        }),
      });
      setUsers((prev) => [data.user, ...prev]);
      setFormValues({ name: "", email: "", password: "" });
    } catch (err) {
      setFormError(err.message || "Failed to create user.");
    }
  };

  /* ---------- Edit user ---------- */
  const openEdit = (user) => {
    setEditingUser(user);
    setEditValues({ name: user.name, email: user.email, password: "" });
    setEditError("");
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditValues((prev) => ({ ...prev, [name]: value }));
  };

  const canSaveEdit =
    editValues.name.trim().length >= 2 &&
    isValidEmail(editValues.email) &&
    (editValues.password === "" || editValues.password.length >= 6);

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!canSaveEdit || !editingUser) return;
    setEditError("");

    const payload = {
      name: editValues.name.trim(),
      email: editValues.email.trim(),
    };
    if (editValues.password) {
      payload.password = editValues.password;
    }

    try {
      const data = await apiRequest(`/users/${editingUser.id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      setUsers((prev) =>
        prev.map((u) => (u.id === editingUser.id ? data.user : u))
      );
      setEditingUser(null);
    } catch (err) {
      setEditError(err.message || "Failed to update user.");
    }
  };

  /* ---------- Delete user ---------- */
  const handleDelete = async () => {
    if (!deletingUser) return;
    try {
      await apiRequest(`/users/${deletingUser.id}`, { method: "DELETE" });
      setUsers((prev) => prev.filter((u) => u.id !== deletingUser.id));
    } catch (err) {
      console.error("Failed to delete user:", err);
    }
    setDeletingUser(null);
  };

  const handleToggleActive = async (user) => {
    const nextActive = !user.active;
    setTogglingId(user.id);
    try {
      const data = await apiRequest(`/users/${user.id}`, {
        method: "PUT",
        body: JSON.stringify({ active: nextActive }),
      });
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, active: data.user.active } : u))
      );
    } catch (err) {
      console.error("Failed to update user active status:", err);
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <Breadcrumb
            className="d-none d-md-inline-block"
            listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}
          >
            <Breadcrumb.Item>
              <FontAwesomeIcon icon={faHome} />
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Users</Breadcrumb.Item>
          </Breadcrumb>
          <h4>Users</h4>
          <p className="mb-0">Manage platform users.</p>
        </div>
      </div>

      <Row>
        {/* ---- Add user form ---- */}
        <Col xl={4} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Add User</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    name="name"
                    value={formValues.name}
                    placeholder="Full name"
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    required
                    type="email"
                    name="email"
                    value={formValues.email}
                    placeholder="name@example.com"
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    required
                    type="password"
                    name="password"
                    value={formValues.password}
                    placeholder="Min 6 characters"
                    onChange={handleChange}
                  />
                </Form.Group>
                {formError ? (
                  <small className="text-danger d-block mb-2">{formError}</small>
                ) : null}
                <Button variant="primary" type="submit" disabled={!canSave}>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add user
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* ---- Users table ---- */}
        <Col xl={8} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">All users</h5>
              <span className="text-muted">{filteredUsers.length} users</span>
            </Card.Header>
            <Card.Body className="pt-0">
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 py-3">
                <Form.Control
                  type="text"
                  placeholder="Search by name or email"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Form.Select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  style={{ maxWidth: 140 }}
                >
                  <option value={5}>5 / page</option>
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                </Form.Select>
              </div>

              <Table responsive className="table-centered align-items-center mb-0">
                <thead className="thead-light">
                  <tr>
                    <th className="border-0">Name</th>
                    <th className="border-0">Email</th>
                    <th className="border-0">Status</th>
                    <th className="border-0">Joined</th>
                    <th className="border-0 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted">
                        Loading...
                      </td>
                    </tr>
                  ) : paginatedUsers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted">
                        {users.length === 0
                          ? "No users yet. Use the form to add one."
                          : "No matches for your search."}
                      </td>
                    </tr>
                  ) : (
                    paginatedUsers.map((user) => (
                      <tr key={user.id}>
                        <td className="fw-bold">{user.name}</td>
                        <td>{user.email}</td>
                        <td>
                          <Badge
                            bg={user.active ? "success" : "secondary"}
                            className="me-2"
                          >
                            {user.active ? "Active" : "Inactive"}
                          </Badge>
                          <Form.Check
                            type="switch"
                            id={`active-${user.id}`}
                            checked={!!user.active}
                            disabled={togglingId === user.id}
                            onChange={() => handleToggleActive(user)}
                            label=""
                          />
                        </td>
                        <td>{formatDate(user.createdAt)}</td>
                        <td className="text-end">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-2"
                            onClick={() => openEdit(user)}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => setDeletingUser(user)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>

              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mt-3">
                <small className="text-muted">
                  Showing {filteredUsers.length === 0 ? 0 : startIndex + 1}
                  {"-"}
                  {Math.min(startIndex + pageSize, filteredUsers.length)} of{" "}
                  {filteredUsers.length}
                </small>
                <Pagination className="mb-0">
                  <Pagination.Prev
                    disabled={safePage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  />
                  {Array.from({ length: totalPages }, (_, i) => {
                    const page = i + 1;
                    if (
                      page !== 1 &&
                      page !== totalPages &&
                      Math.abs(page - safePage) > 1
                    ) {
                      return null;
                    }
                    return (
                      <Pagination.Item
                        key={page}
                        active={page === safePage}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  })}
                  <Pagination.Next
                    disabled={safePage >= totalPages}
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                  />
                </Pagination>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ---- Edit modal ---- */}
      <Modal show={!!editingUser} onHide={() => setEditingUser(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit user</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                required
                type="text"
                name="name"
                value={editValues.name}
                onChange={handleEditChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                required
                type="email"
                name="email"
                value={editValues.email}
                onChange={handleEditChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Password (leave blank to keep current)</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={editValues.password}
                placeholder="New password"
                onChange={handleEditChange}
              />
            </Form.Group>
            {editError ? (
              <small className="text-danger d-block">{editError}</small>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setEditingUser(null)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={!canSaveEdit}>
              Save changes
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ---- Delete confirm modal ---- */}
      <Modal show={!!deletingUser} onHide={() => setDeletingUser(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete user</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <strong>{deletingUser?.name}</strong> ({deletingUser?.email})?
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeletingUser(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
